import React, { useState } from 'react';
import { Typography, Box, Paper, LinearProgress, Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Modal, TextField } from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

const UnitDetails = () => {
  const { projectId, unitName } = useParams();
  const navigate = useNavigate();
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [stepFormData, setStepFormData] = useState({
    title: '',
    startDate: '',
    endDate: ''
  });
  const [isEditingStep, setIsEditingStep] = useState(false);
  
  const projects = {
    1: { name: 'Ship Alpha', status: 'Active', progress: 75 },
    2: { name: 'Ship Beta', status: 'Planning', progress: 25 },
    3: { name: 'Ship Gamma', status: 'Completed', progress: 100 }
  };
  
  const project = projects[projectId];
  
  // Przykładowe dane dla UNIT
  const unitData = {
    name: unitName,
    progress: 67,
    status: 'In Progress',
    startDate: '2024-01-15',
    endDate: '2024-06-30',
    location: 'Dock 2',
    assignedTeam: 'Team Alpha',
    materials: ['Steel Plates', 'Welding Rods', 'Paint', 'Electronics'],
    description: `Detailed specifications and work instructions for ${unitName}. This unit is a critical component of the ship construction process.`
  };
  
  const stepProgressData = [85, 92, 78, 65, 45, 30, 15, 8, 0, 0, 0, 0];
  const subStepProgressData = {
    'Preparation': [95, 88, 92, 85],
    'Cutting': [100, 95, 90, 85],
    'Prefabrication': [85, 80, 75, 70],
    'Section Assembly': [70, 65, 60],
    'Measurements': [50, 45, 40],
    'Main Welding': [35, 30],
    'Visual Testing (VT)': [20],
    'NDT Testing': [15, 10, 8, 5],
    'Post-execution Measurement': [0, 0],
    'Painting': [0, 0, 0],
    'Punch List': [0, 0],
    'Handover': [0, 0]
  };
  

  
  const handleEditTask = (taskName) => {
    navigate(`/projects/${projectId}/unit/${unitName}/task?mode=edit&task=${encodeURIComponent(taskName)}`);
  };
  
  const handleAddTask = () => {
    navigate(`/projects/${projectId}/unit/${unitName}/task?mode=add`);
  };
  
  const handleDeleteClick = (itemName) => {
    setItemToDelete(itemName);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    if (itemToDelete) {
      setHiddenItems([...hiddenItems, itemToDelete]);
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };
  
  const handleEditStep = (stepName) => {
    setIsEditingStep(true);
    setStepFormData({
      title: stepName,
      startDate: '2024-01-15',
      endDate: '2024-01-22'
    });
    setStepModalOpen(true);
  };
  
  const handleAddStep = () => {
    setIsEditingStep(false);
    setStepFormData({
      title: '',
      startDate: '',
      endDate: ''
    });
    setStepModalOpen(true);
  };
  
  const handleStepModalClose = () => {
    setStepModalOpen(false);
    setStepFormData({ title: '', startDate: '', endDate: '' });
  };
  
  const handleStepSave = () => {
    console.log('Saving step:', stepFormData);
    handleStepModalClose();
  };
  

  
  const stepDetails = {
    'Preparation': {
      title: 'Production Engineering and Technology',
      subSteps: [
        'Shop drawings',
        'Section division plan',
        'Welding sequence plan (WPS)',
        'Nesting and cutting plan'
      ]
    },
    'Cutting': {
      title: 'Material Preparation',
      subSteps: [
        'Steel reception',
        'Cleaning and coating application',
        'Nesting and marking',
        'Cutting'
      ]
    },
    'Prefabrication': {
      title: 'Prefabrication (Pre-assembly)',
      subSteps: [
        'Bending and rolling',
        'Sub-assembly',
        'Panel production',
        'Grid assembly'
      ]
    },
    'Section Assembly': {
      title: 'Main Section Assembly (Unit Assembly)',
      subSteps: [
        'Assembly on section station',
        'Alignment and fairing',
        'Geodetic measurements'
      ]
    },
    'Measurements': {
      title: 'Geodetic Measurements',
      subSteps: [
        'Overall section dimensions',
        'Straightness',
        'Position of main structural elements'
      ]
    },
    'Main Welding': {
      title: 'Main Welding',
      subSteps: [
        'Final welding',
        'Weld cleaning'
      ]
    },
    'Visual Testing (VT)': {
      title: 'Quality Control and NDT',
      subSteps: [
        'Visual Testing (VT)'
      ]
    },
    'NDT Testing': {
      title: 'NDT Testing (Non-Destructive Testing)',
      subSteps: [
        'Magnetic Particle (MT) / Penetrant Testing (PT)',
        'Radiographic Testing (RT)',
        'Ultrasonic Testing (UT)',
        'Correction and repair'
      ]
    },
    'Post-execution Measurement': {
      title: 'Post-execution Measurements',
      subSteps: [
        'Final measurements of entire section',
        'Tolerance confirmation'
      ]
    },
    'Painting': {
      title: 'Finishing and Handover',
      subSteps: [
        'Cleaning and painting',
        'Pre-assembly',
        'Final section punch list'
      ]
    },
    'Punch List': {
      title: 'Final Section Punch List',
      subSteps: [
        'Section inspection by inspector',
        'Creating punch list for specific section'
      ]
    },
    'Handover': {
      title: 'Handover to Dock/Slipway',
      subSteps: [
        'Closing all critical items',
        'Crane transport and connection with rest of ship'
      ]
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Pending': return 'warning';
      case 'On Hold': return 'error';
      default: return 'default';
    }
  };
  
  if (!project) {
    return <Typography>Project not found</Typography>;
  }
  
  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {unitData.name} - Unit Details
      </Typography>
      
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Project: {project.name}
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 3, height: 200 }}>
            <Typography variant="h6" gutterBottom>
              Unit Information
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Start Date:</strong> {unitData.startDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>End Date:</strong> {unitData.endDate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Location:</strong> {unitData.location}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Assigned Team:</strong> {unitData.assignedTeam}
              </Typography>
            </Box>
          </Paper>
          
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Production Process
              </Typography>
              <Button
                variant="contained"
                color="success"
                startIcon={<Add />}
                size="small"
                onClick={handleAddStep}
              >
                Add New
              </Button>
            </Box>
            
            {[
              'Preparation',
              'Cutting', 
              'Prefabrication',
              'Section Assembly',
              'Measurements',
              'Main Welding',
              'Visual Testing (VT)',
              'NDT Testing',
              'Post-execution Measurement',
              'Painting',
              'Punch List',
              'Handover'
            ].filter(step => !hiddenItems.includes(step)).map((step, index) => {
              const startDate = new Date(Date.now() + index * 7 * 24 * 60 * 60 * 1000).toLocaleDateString();
              const endDate = new Date(Date.now() + (index + 1) * 7 * 24 * 60 * 60 * 1000).toLocaleDateString();
              const progress = stepProgressData[index];
              
              return (
                <Box 
                  key={index}
                  onClick={() => setSelectedStep(step)}
                  sx={{ 
                    p: 2,
                    border: selectedStep === step ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    backgroundColor: selectedStep === step ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '&:hover': { 
                      backgroundColor: selectedStep === step ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {step}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        sx={{ color: '#ed6c02' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditStep(step);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{ color: '#d32f2f' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(step);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Planned: {startDate} - {endDate}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progress}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={progress} 
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Box>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 3, height: 200 }}>
            <Typography variant="h6" gutterBottom>
              Progress & Status
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Progress</Typography>
                <Typography variant="body2" fontWeight="bold">{unitData.progress}%</Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={unitData.progress} 
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Status
              </Typography>
              <Chip 
                label={unitData.status} 
                color={getStatusColor(unitData.status)}
                size="small"
              />
            </Box>
          </Paper>
          
          {selectedStep && stepDetails[selectedStep] && (
            <Paper sx={{ p: 3, flex: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {selectedStep}
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Add />}
                  size="small"
                  onClick={handleAddTask}
                >
                  Add New
                </Button>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                {stepDetails[selectedStep].subSteps.filter(subStep => !hiddenItems.includes(subStep)).map((subStep, index) => {
                  const baseDate = new Date('2024-01-15');
                  const startDate = new Date(baseDate.getTime() + index * 3 * 24 * 60 * 60 * 1000).toLocaleDateString();
                  const endDate = new Date(baseDate.getTime() + (index + 1) * 3 * 24 * 60 * 60 * 1000).toLocaleDateString();
                  const progress = subStepProgressData[selectedStep][index] || 0;
                  
                  return (
                    <Box 
                      key={index}
                      onClick={() => handleEditTask(subStep)}
                      sx={{ 
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': { 
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {subStep}
                        </Typography>
                        <IconButton
                          size="small"
                          sx={{ color: '#d32f2f' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(subStep);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Release: {startDate} - {endDate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {progress}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={progress} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              

            </Paper>
          )}
        </Box>
      </Box>
      

      
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activity Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Action:</strong> {selectedLog.action}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Date:</strong> {selectedLog.date}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>User:</strong> {selectedLog.user}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Details:</strong> {selectedLog.details}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <strong>Note:</strong> {selectedLog.note}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{itemToDelete}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      <Modal open={stepModalOpen} onClose={handleStepModalClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4
        }}>
          <Typography variant="h6" gutterBottom>
            {isEditingStep ? 'Edit Process Step' : 'Add New Process Step'}
          </Typography>
          
          <TextField
            fullWidth
            label="Title"
            value={stepFormData.title}
            onChange={(e) => setStepFormData({...stepFormData, title: e.target.value})}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Planned Start Date"
            type="date"
            value={stepFormData.startDate}
            onChange={(e) => setStepFormData({...stepFormData, startDate: e.target.value})}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            fullWidth
            label="Planned End Date"
            type="date"
            value={stepFormData.endDate}
            onChange={(e) => setStepFormData({...stepFormData, endDate: e.target.value})}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button onClick={handleStepModalClose}>Cancel</Button>
            <Button variant="contained" onClick={handleStepSave}>Save</Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default UnitDetails;